import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cls } from "app/utils";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import ButtonVariant from "app/components/Button/ButtonVariant";
import UserAvatar from "app/components/UserAvatar";

import FamilyMemberModal from "app/routes/Account/FamilyEdit/_components/FamilyMemberModal";
import {
  createFamilyMember,
  familyMembershipErrorMessage,
} from "app/routes/Account/FamilyEdit/utils";
import {
  FamilyMemberFormData,
  FamilyMemberSubmitResult,
} from "app/routes/Account/FamilyEdit/types";

import styles from "./styles.module.scss";

export interface BookingCandidate {
  /** participantAthleteId sent to the booking endpoint; null = the head books for themselves. */
  id: string | null;
  name: string;
  avatar: string | null;
  /** "" for self; "Child · Age 14" / "Spouse · Age 42" for managed members. */
  subLabel: string;
}

interface Props {
  isOpen: boolean;
  candidates: BookingCandidate[];
  onClose: () => void;
  onConfirm: (participantAthleteId: string | null) => void;
  /** Refresh the roster after a member is created on the spot (CFTB-3277). */
  onMemberAdded: () => Promise<void> | void;
  /** Heading — defaults to the booking copy; offerings pass "Who is this Package for?" (CFTB-3285). */
  title?: string;
  /** Sub-label above the candidate list. */
  candidatesLabel?: string;
  /**
   * Pre-selected candidate id when the picker opens (CN-2566): the member being
   * viewed on the bookings page, so booking for a child defaults to that child
   * rather than always the head. null / omitted falls back to self.
   */
  defaultParticipantId?: string | null;
}

/**
 * "Who is this for?" picker (CN-1323). Opened when the athlete manages family
 * members; the chosen candidate's participant id is carried through to the
 * booking/purchase POST. A regular athlete with no managed members never sees
 * it. Shared by session booking and offering purchase (CFTB-3285).
 */
const ParticipantPickerModal = ({
  isOpen,
  candidates,
  onClose,
  onConfirm,
  onMemberAdded,
  title,
  candidatesLabel,
  defaultParticipantId,
}: Props) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultParticipantId ?? candidates[0]?.id ?? null,
  );
  const [showAddModal, setShowAddModal] = useState(false);

  // Default to the viewed member (CN-2566), or the first candidate (self) when
  // none is passed, each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelectedId(defaultParticipantId ?? candidates[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultParticipantId]);

  // Create a family member without leaving the booking flow (CFTB-3277): refresh
  // the roster, pre-select the new member, and drop back into the picker.
  const handleCreateMember = async (
    data: FamilyMemberFormData,
  ): Promise<FamilyMemberSubmitResult> => {
    try {
      const created = await createFamilyMember(data);
      await onMemberAdded();
      setSelectedId(created.userId);
      setShowAddModal(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: familyMembershipErrorMessage(err) ?? undefined };
    }
  };

  return (
    <>
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.popup}>
      <div className={styles.container}>
        <i className={cls("ico ico-x", styles.close)} onClick={onClose} />

        <div className={styles.body}>
          <h2 className={styles.title}>
            {title ?? t("Who is this session for?")}
          </h2>

          <div className={styles.inner}>
            <div className={styles.candidates}>
              <span className={styles.candidates_label}>
                {candidatesLabel ?? t("Booking Candidates")}
              </span>
              <div className={styles.list}>
                {candidates.map((candidate) => {
                  const selected = selectedId === candidate.id;
                  return (
                    <button
                      key={candidate.id ?? "self"}
                      type="button"
                      className={styles.row}
                      onClick={() => setSelectedId(candidate.id)}
                    >
                      <span
                        className={cls(styles.radio, {
                          [styles.radio_selected]: selected,
                        })}
                      >
                        <span className={styles.radio_dot} />
                      </span>
                      <UserAvatar
                        sizeWidth={48}
                        sizeHeight={48}
                        altName={candidate.name}
                        srcName={candidate.avatar ?? "default"}
                        userName={candidate.name}
                        className={cls("avatar", styles.avatar)}
                      />
                      <span className={styles.text}>
                        <span className={styles.name}>{candidate.name}</span>
                        {candidate.subLabel && (
                          <span className={styles.sub_label}>
                            {candidate.subLabel}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className={styles.add_member}
              onClick={() => setShowAddModal(true)}
            >
              {t("+ Add New Family Member")}
            </button>
          </div>
        </div>

        <div className={styles.footer}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="secondary-filled"
            buttonStyles={styles.pill}
            onClickFunc={onClose}
          />
          <ButtonVariant
            title={t("Confirm")}
            buttonType="primary"
            buttonStyles={styles.pill}
            onClickFunc={() => onConfirm(selectedId)}
          />
        </div>
      </div>
    </ModalLayout>

      <FamilyMemberModal
        isOpen={showAddModal}
        mode="create"
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateMember}
      />
    </>
  );
};

export default ParticipantPickerModal;
